/**
 * Parent hook — owns `candidates` array + chip visibility for directory thumbs.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  resolveConsumerSquareLogoForTheme,
  type LogoChipBackgroundSettings,
  type LogoChipRimSettings,
  type LogoChipRimTheme,
} from 'pi-kiosk-shared';
import {
  buildOrgDirectoryCandidates,
  buildStoreDirectoryCandidates,
  dropFailedDirectoryCandidate,
  type DirectoryMediaCandidate,
} from './directoryCascade.js';
import type { DirectoryMonogramInput } from './directoryMonogram.js';
import type { DirectoryEntityThumbProps } from './DirectoryEntityThumb.js';

export interface UseStoreDirectoryThumbInput {
  readonly imageUrl?: string | null;
  readonly imageUrlDark?: string | null;
  readonly tenantLogoUrl?: string | null;
  readonly tenantLogoUrlDark?: string | null;
  readonly monogram: DirectoryMonogramInput;
  /**
   * Platform Obchody with SP photo primary → true.
   * Oblíbené / tenant-scoped Obchody / orgs → false.
   */
  readonly showTenantChip: boolean;
  readonly loading?: DirectoryEntityThumbProps['loading'];
  readonly fetchPriority?: DirectoryEntityThumbProps['fetchPriority'];
  /** Default `md`. Obchody passes `lg` (~1.5× store photo). */
  readonly size?: DirectoryEntityThumbProps['size'];
  /**
   * Dual-regime brand-kit rim for brandMark fallback (G4/G10).
   * Obchody storePhoto overlay chip keeps `--directory-thumb-chip-ring` — not this.
   */
  readonly logoChipRim?: LogoChipRimSettings | null;
  /** Dual-regime brand-kit background for brandMark transparent pad. */
  readonly logoChipBackground?: LogoChipBackgroundSettings | null;
  /** Active theme for rim/background/URL resolution — pass `useTheme().effectiveTheme`. */
  readonly logoChipRimTheme?: LogoChipRimTheme;
  /** Fired after cascade advance (e.g. invalidate signed list cache). */
  readonly onMediaFailed?: (failedSrc: string) => void;
  readonly testId?: string;
}

export interface UseDirectoryThumbResult {
  readonly candidates: readonly DirectoryMediaCandidate[];
  readonly thumbProps: DirectoryEntityThumbProps;
}

export function useStoreDirectoryThumb(
  input: UseStoreDirectoryThumbInput,
): UseDirectoryThumbResult {
  const [excludeSrcs, setExcludeSrcs] = useState<ReadonlySet<string>>(() => new Set());
  /** When equal to current media key, chip is hidden; URL refresh changes key → chip retries. */
  const mediaKey = `${input.imageUrl ?? ''}\0${input.imageUrlDark ?? ''}\0${input.tenantLogoUrl ?? ''}\0${input.tenantLogoUrlDark ?? ''}\0${input.logoChipRimTheme ?? 'light'}`;
  const [chipFailedForKey, setChipFailedForKey] = useState<string | null>(null);
  const chipFailed = chipFailedForKey === mediaKey;
  const theme = input.logoChipRimTheme ?? 'light';

  const candidates = useMemo(
    () =>
      buildStoreDirectoryCandidates({
        imageUrl: input.imageUrl,
        imageUrlDark: input.imageUrlDark,
        tenantLogoUrl: input.tenantLogoUrl,
        tenantLogoUrlDark: input.tenantLogoUrlDark,
        theme,
        excludeSrcs,
      }),
    [
      excludeSrcs,
      input.imageUrl,
      input.imageUrlDark,
      input.tenantLogoUrl,
      input.tenantLogoUrlDark,
      theme,
    ],
  );

  const onMediaFailed = input.onMediaFailed;

  const onImageError = useCallback(
    (failedSrc: string) => {
      setExcludeSrcs((prev) => {
        if (prev.has(failedSrc)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(failedSrc);
        return next;
      });
      onMediaFailed?.(failedSrc);
    },
    [onMediaFailed],
  );

  const onChipError = useCallback(
    (_failedSrc: string) => {
      setChipFailedForKey(mediaKey);
    },
    [mediaKey],
  );

  const primaryIsStorePhoto = candidates[0]?.profile === 'storePhoto';
  const showTenantChip =
    input.showTenantChip && primaryIsStorePhoto && !chipFailed;
  const tenantChipSrc = showTenantChip
    ? resolveConsumerSquareLogoForTheme(
        {
          logoUrl: input.tenantLogoUrl ?? null,
          logoUrlDark: input.tenantLogoUrlDark ?? null,
        },
        theme,
      )
    : null;

  return {
    candidates,
    thumbProps: {
      candidates,
      monogram: input.monogram,
      showTenantChip,
      tenantChipSrc,
      onImageError,
      onChipError,
      loading: input.loading,
      fetchPriority: input.fetchPriority,
      size: input.size,
      logoChipRim: input.logoChipRim,
      logoChipBackground: input.logoChipBackground,
      logoChipRimTheme: theme,
      brandMarkLogo: {
        logoUrl: input.tenantLogoUrl ?? null,
        logoUrlDark: input.tenantLogoUrlDark ?? null,
      },
      testId: input.testId,
    },
  };
}

export interface UseOrgDirectoryThumbInput {
  readonly logoUrl?: string | null;
  readonly logoUrlDark?: string | null;
  readonly monogram: DirectoryMonogramInput;
  readonly loading?: DirectoryEntityThumbProps['loading'];
  readonly fetchPriority?: DirectoryEntityThumbProps['fetchPriority'];
  /** Default `md`. Organizace / Obchody pass `lg`. */
  readonly size?: DirectoryEntityThumbProps['size'];
  /**
   * When true, media uses object-cover fill (legacy). Prefer omit for brandMark
   * square thumbs so transparent pad + object-contain show card surface.
   */
  readonly fillBox?: boolean;
  /** Dual-regime brand-kit rim from public tenant row (G8/G10). */
  readonly logoChipRim?: LogoChipRimSettings | null;
  /** Dual-regime brand-kit background for brandMark transparent pad. */
  readonly logoChipBackground?: LogoChipBackgroundSettings | null;
  /** Active theme for rim/background/URL resolution — pass `useTheme().effectiveTheme`. */
  readonly logoChipRimTheme?: LogoChipRimTheme;
  readonly onMediaFailed?: (failedSrc: string) => void;
  readonly testId?: string;
}

export function useOrgDirectoryThumb(input: UseOrgDirectoryThumbInput): UseDirectoryThumbResult {
  const [excludeSrcs, setExcludeSrcs] = useState<ReadonlySet<string>>(() => new Set());
  const theme = input.logoChipRimTheme ?? 'light';

  const candidates = useMemo(
    () =>
      buildOrgDirectoryCandidates({
        logoUrl: input.logoUrl,
        logoUrlDark: input.logoUrlDark,
        theme,
        excludeSrcs,
      }),
    [excludeSrcs, input.logoUrl, input.logoUrlDark, theme],
  );

  const onMediaFailed = input.onMediaFailed;

  const onImageError = useCallback(
    (failedSrc: string) => {
      setExcludeSrcs((prev) => {
        if (prev.has(failedSrc)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(failedSrc);
        return next;
      });
      onMediaFailed?.(failedSrc);
    },
    [onMediaFailed],
  );

  return {
    candidates,
    thumbProps: {
      candidates,
      monogram: input.monogram,
      showTenantChip: false,
      tenantChipSrc: null,
      onImageError,
      loading: input.loading,
      fetchPriority: input.fetchPriority,
      size: input.size,
      fillBox: input.fillBox,
      logoChipRim: input.logoChipRim,
      logoChipBackground: input.logoChipBackground,
      logoChipRimTheme: theme,
      brandMarkLogo: {
        logoUrl: input.logoUrl ?? null,
        logoUrlDark: input.logoUrlDark ?? null,
      },
      testId: input.testId,
    },
  };
}

/** Pure helper for parents that manage candidates without the hook. */
export function advanceCandidatesOnError(
  candidates: readonly DirectoryMediaCandidate[],
  failedSrc: string,
): DirectoryMediaCandidate[] {
  return dropFailedDirectoryCandidate(candidates, failedSrc);
}
